import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import HexagonLoader from '@/components/HexagonLoader/HexagonLoader';
import { usePrintMode } from './PrintModeContext';
import * as Sentry from '@sentry/react';

/**
 * Microsoft 365 Configuration & Compliance Gaps.
 *
 * Consolidates configuration posture across Microsoft Purview, Intune, Defender and the
 * Endpoint Health monitor, then derives a list of *configuration inadequacies* so the IT
 * team can see exactly where 365 services need fixing or hardening.
 *
 * Every finding is derived at runtime from live service data — nothing is hardcoded.
 * Services that return no data are reported as "not reporting" rather than assumed healthy.
 */
const Microsoft365Compliance = () => {
  const { apiFetch } = useAuth();
  const isPrintMode = usePrintMode();
  const [loading, setLoading] = useState(true);
  const [purview, setPurview] = useState(null);
  const [intune, setIntune] = useState(null);
  const [defender, setDefender] = useState(null);
  const [endpointStats, setEndpointStats] = useState(null);
  const [unavailable, setUnavailable] = useState([]);

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line

  async function fetchAll() {
    setLoading(true);
    const missing = [];
    try {
      const [p, i, d, e] = await Promise.allSettled([
        apiFetch('/api/mspurview/summary'),
        apiFetch('/api/Intune/device-inventory-summary'),
        apiFetch('/api/Defender/stats'),
        apiFetch('/api/EndpointHealth/alerts/statistics')
      ]);

      if (p.status === 'fulfilled' && p.value.ok && p.value.status !== 204) {
        setPurview(await p.value.json().catch(() => null));
      } else { missing.push('Microsoft Purview'); }

      if (i.status === 'fulfilled' && i.value.ok && i.value.status !== 204) {
        setIntune(await i.value.json().catch(() => null));
      } else { missing.push('Intune'); }

      if (d.status === 'fulfilled' && d.value.ok && d.value.status !== 204) {
        setDefender(await d.value.json().catch(() => null));
      } else { missing.push('Microsoft Defender'); }

      if (e.status === 'fulfilled' && e.value.ok && e.value.status !== 204) {
        setEndpointStats(await e.value.json().catch(() => null));
      } else { missing.push('Endpoint Health Monitor'); }

      setUnavailable(missing);
    } catch (ex) {
      Sentry.captureException(ex);
    } finally {
      setLoading(false);
    }
  }

  const num = (v) => (typeof v === 'number' ? v : 0);
  const getCompliance = (data) => {
    if (!data) return null;
    const total = data.totalDevices ?? data.TotalDevices ?? 0;
    const states = data.complianceStates ?? data.ComplianceStates ?? {};
    const compliant = states.Compliant ?? states.compliant ?? 0;
    const rate = data.previousMonthComplianceRate ?? data.PreviousMonthComplianceRate ??
      (total > 0 ? (compliant / total) * 100 : 0);
    return { total, compliant, rate };
  };

  // Derive configuration inadequacies dynamically from live data.
  const findings = useMemo(() => {
    const list = [];
    const add = (service, severity, title, detail) => list.push({ service, severity, title, detail });

    // ── Microsoft Purview ──
    if (purview) {
      const p = purview;
      // hasActiveDlpPolicies is one-sided: "false" only means no policy was found scoped to this
      // app's own activity - it can't see policies scoped to Exchange/Teams/SharePoint/Endpoint,
      // which is how DLP is normally configured (see MsPurviewService.cs GetCompliancePoliciesAsync
      // doc comment, verified against this tenant's real Purview policies). Flag as a low-severity
      // "verify" item rather than a confirmed high-severity gap.
      if (p.hasActiveDlpPolicies === false)
        add('Purview', 'low', 'DLP policy status not confirmed', p.compliancePolicyScopeCaveat || 'This integration did not detect a DLP policy scoped to its own activity. Verify actual DLP policy status directly in the Purview compliance portal (Data Loss Prevention → Policies).');
      if (p.hasSensitivityLabels === false)
        add('Purview', 'high', 'No sensitivity labels configured', 'Information cannot be classified or protected. Configure and publish sensitivity labels.');
      else if (num(p.labelsWithEncryption) === 0 && num(p.enabledSensitivityLabels) > 0)
        add('Purview', 'medium', 'No sensitivity labels apply encryption', 'Labels exist but none enforce encryption. Confidential labels should encrypt content at rest.');
      if (p.hasRetentionPolicies === false)
        add('Purview', 'medium', 'No retention policies configured', 'Records retention/deletion is not governed. This is a compliance and legal-hold risk.');
      else if (p.retentionDataAvailable === false)
        add('Purview', 'low', 'Retention policy status not verifiable', 'Microsoft Graph\'s retention label API requires delegated (signed-in user) auth, which this integration does not use. Verify retention configuration directly in the Purview compliance portal.');
      if (num(p.labelCoverageRate) > 0 && num(p.labelCoverageRate) < 50)
        add('Purview', 'medium', `Low sensitivity label coverage (${num(p.labelCoverageRate)}%)`, `Only ${num(p.enabledSensitivityLabels)} of ${num(p.totalSensitivityLabels)} labels are enabled.`);
      if (num(p.openDlpAlerts) > 0 && num(p.dlpAlertResolutionRate) < 50)
        add('Purview', 'medium', `${num(p.openDlpAlerts)} DLP alerts unresolved`, `Resolution rate is ${num(p.dlpAlertResolutionRate)}%. Triage and remediate open DLP alerts.`);

      // Microsoft Secure Score - well-rounded security posture metric, added as a
      // supplementary data point since retention label data cannot be retrieved under this
      // app's authentication model (Delegated-only Graph API vs. app-only client-credentials).
      if (p.hasSecureScoreData === false)
        add('Purview', 'low', 'Microsoft Secure Score not reporting', 'Secure Score data was unavailable from Microsoft Graph. Verify SecurityEvents.Read.All permission and admin consent.');
      else if (p.hasSecureScoreData) {
        const pct = num(p.secureScorePercentage);
        if (pct < 40)
          add('Purview', 'high', `Microsoft Secure Score is low (${pct}%)`, `Tenant security configuration score is ${p.secureScoreCurrent}/${p.secureScoreMax}. Review and action top recommendations in Microsoft Defender.`);
        else if (pct < 70)
          add('Purview', 'medium', `Microsoft Secure Score needs improvement (${pct}%)`, `Tenant security configuration score is ${p.secureScoreCurrent}/${p.secureScoreMax}. Review category breakdown for quick wins.`);
        else if (p.secureScoreIndustryAverage != null && p.secureScoreCurrent < p.secureScoreIndustryAverage)
          add('Purview', 'low', 'Secure Score below industry peer average', `Current score ${p.secureScoreCurrent} is below the industry average of ${Math.round(p.secureScoreIndustryAverage)}.`);
      }
    }

    // ── Intune (device compliance config) ──
    const comp = getCompliance(intune);
    if (comp) {
      if (comp.total === 0)
        add('Intune', 'high', 'No managed devices enrolled', 'Intune reports zero devices. Endpoints are unmanaged — enrol devices and apply compliance policies.');
      else if (comp.rate < 80)
        add('Intune', comp.rate < 60 ? 'high' : 'medium', `Device compliance at ${comp.rate.toFixed(0)}%`, `${comp.compliant} of ${comp.total} devices are compliant. Review compliance policies and remediate failing devices.`);
      const states = intune.complianceStates ?? intune.ComplianceStates ?? {};
      const unknown = states.Unknown ?? states.unknown ?? 0;
      if (unknown > 0)
        add('Intune', 'low', `${unknown} devices in Unknown compliance state`, 'Devices have not checked in or lack a compliance policy assignment.');
    }

    // ── Defender ──
    if (defender) {
      const high = num(defender.highSeverityAlerts);
      const open = num(defender.openAlerts);
      if (high > 0)
        add('Defender', 'high', `${high} high-severity alerts open`, 'Unresolved high-severity endpoint alerts require immediate investigation.');
      else if (open > 0)
        add('Defender', 'medium', `${open} endpoint alerts open`, 'Outstanding Defender alerts should be triaged to closure.');
    }

    // ── Endpoint Health Monitor ──
    if (endpointStats) {
      const active = num(endpointStats.totalActive);
      if (active > 0) {
        const byType = (endpointStats.byMetricType || [])
          .slice()
          .sort((a, b) => num(b.count) - num(a.count));
        const top = byType[0];
        add('Endpoint Health', active > 10 ? 'high' : 'medium',
          `${active} active endpoint health alert${active === 1 ? '' : 's'}`,
          top ? `Most common: ${top.metricType} (${top.count}).` : 'Review active endpoint health alerts.');
      }
    }

    // Sort by severity for display
    const rank = { high: 0, medium: 1, low: 2 };
    return list.sort((a, b) => rank[a.severity] - rank[b.severity]);
  }, [purview, intune, defender, endpointStats]);

  const severityCounts = useMemo(() => ({
    high: findings.filter(f => f.severity === 'high').length,
    medium: findings.filter(f => f.severity === 'medium').length,
    low: findings.filter(f => f.severity === 'low').length,
  }), [findings]);

  const llmProxyText = useMemo(() => {
    const lines = [`Microsoft 365 Config Gaps: ${severityCounts.high} high, ${severityCounts.medium} medium, ${severityCounts.low} low`];
    findings.forEach(f => lines.push(`[${f.severity.toUpperCase()}] ${f.service}: ${f.title} — ${f.detail}`));
    if (unavailable.length) lines.push(`Not reporting: ${unavailable.join(', ')}`);
    return lines.join('\n');
  }, [findings, severityCounts, unavailable]);

  if (loading) return <HexagonLoader message="Loading Microsoft 365 configuration posture..." />;

  const sevColor = (s) => (s === 'high' ? '#d32f2f' : s === 'medium' ? '#ffa000' : '#7cb342');
  const sevIcon = (s) => (s === 'high' ? '✗' : s === 'medium' ? '⚠' : 'ℹ');

  return (
    <div className={`dashboard-card group-wrapper m365-compliance-summary${isPrintMode ? ' print-mode' : ''}`}>
      <div data-llm-proxy style={{ display: 'none' }}>{llmProxyText}</div>
      <h2 style={{ marginTop: 0, marginBottom: 12 }}>Microsoft 365 Configuration &amp; Compliance Gaps</h2>

      {/* Summary banner */}
      <div className="dashboard-card exec-banner" style={{ borderLeft: `4px solid ${findings.length === 0 ? '#2e7d32' : sevColor(findings[0].severity)}` }}>
        <div className="exec-banner-text">
          {findings.length === 0 ? (
            <span><strong style={{ color: '#2e7d32' }}>✓ No configuration gaps detected</strong> across the Microsoft 365 services that are reporting.</span>
          ) : (
            <span>
              <strong>{findings.length} configuration gap{findings.length === 1 ? '' : 's'}</strong> identified that may put the tenant at risk
              {' '}(<span style={{ color: '#d32f2f', fontWeight: 600 }}>{severityCounts.high} high</span>,
              {' '}<span style={{ color: '#ffa000', fontWeight: 600 }}>{severityCounts.medium} medium</span>,
              {' '}<span style={{ color: '#7cb342', fontWeight: 600 }}>{severityCounts.low} low</span>).
            </span>
          )}
        </div>
      </div>

      {/* Findings list */}
      {findings.length > 0 && (
        <div className="dashboard-card" style={{ padding: 0 }}>
          <table className="data-table" style={{ width: '100%', textAlign: 'left' }}>
            <thead>
              <tr>
                <th style={{ width: 70 }}>Severity</th>
                <th style={{ width: 130 }}>Service</th>
                {/* Explicit width rather than leaving this column unset: in
                    print, table-layout:auto in Chrome's headless print
                    renderer doesn't reliably hand this column "the rest" of
                    the table width the way it does on-screen - it was
                    rendering narrow with a large unused gap between the
                    Service column and this one. A percentage width didn't
                    fix it either (the GitHub Month-over-Month table's fix
                    only started working once ALL of its columns used the
                    same unit - pixels - so mixing px siblings with a %
                    column here was likely the same problem). Use px to
                    match Severity/Service. */}
                <th style={{ width: 480 }}>Finding</th>
              </tr>
            </thead>
            <tbody>
              {findings.map((f, idx) => (
                <tr key={idx}>
                  <td>
                    <span className="inline-tag" style={{ background: `${sevColor(f.severity)}20`, color: sevColor(f.severity), fontWeight: 600 }}>
                      {sevIcon(f.severity)} {f.severity}
                    </span>
                  </td>
                  <td style={{ fontWeight: 600 }}>{f.service}</td>
                  <td>
                    <div style={{ fontWeight: 600 }}>{f.title}</div>
                    <div style={{ fontSize: 12, color: '#666' }}>{f.detail}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Services not reporting — a gap in its own right */}
      {unavailable.length > 0 && (
        <div className="explanation-box" style={{ borderLeft: '4px solid #ffa000' }}>
          <strong>Services not currently reporting:</strong> {unavailable.join(', ')}.
          <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
            A service that cannot report its configuration is itself a visibility gap — verify licensing, credentials and connectivity for these integrations.
          </div>
        </div>
      )}

      <div className="explanation-box">
        <strong>How to read this:</strong>
        <ul>
          <li><strong>High</strong> — a protection control is missing or failing (e.g. no DLP, unmanaged devices, high-severity alerts). Address urgently.</li>
          <li><strong>Medium</strong> — partial coverage or unresolved items that weaken the security posture.</li>
          <li><strong>Low</strong> — informational gaps worth tidying up.</li>
          <li>All findings are derived live from Purview, Intune, Defender and the Endpoint Health monitor — nothing is assumed.</li>
        </ul>
      </div>
    </div>
  );
};

export default Microsoft365Compliance;
